import { format, parseISO } from "date-fns";
import { be, fr } from "date-fns/locale";

type DateLocale = "fr" | "be";
type DateFormat = "short" | "long";

type FormatOptions = {
  locale?: DateLocale;
  format?: DateFormat;
  withTime?: boolean;
};

type DateTimePatterns = {
  short: {
    date: string;
    time: string;
    datetime: string;
    table: string;
  };
  long: {
    date: string;
    time: string;
    datetime: string;
    dashboard: string;
  };
};
const formatPatterns: DateTimePatterns = {
  short: {
    date: "dd/MM/yyyy",
    time: "HH:mm",
    datetime: "dd/MM/yyyy HH:mm",
    table: "dd/MM/yy HH:mm:ss",
  },
  long: {
    date: "dd MMMM yyyy",
    time: "HH:mm",
    datetime: "dd MMMM yyyy à HH:mm",
    dashboard: "EEEE d MMMM - HH'h'mm",
  },
};

/**
 * Formats a date string or Date object according to French or Belgian locale
 * @param dateOrString - Date to format (string or Date object)
 * @param options - Formatting options
 * @returns Formatted date string
 */
export function dateFormat(dateOrString: Date | string, options: FormatOptions = {}): string {
  const { locale = "fr", format: dateFormat = "long", withTime = true } = options;

  try {
    const date = typeof dateOrString === "string" ? parseISO(dateOrString) : dateOrString;
    const localeObj = locale === "be" ? be : fr;
    const pattern = formatPatterns[dateFormat][withTime ? "datetime" : "date"];
    return format(date, pattern, { locale: localeObj });
  } catch (_error) {
    return "Erreur de date";
  }
}

export function formatDashboardHeader(date: Date | string, locale: DateLocale = "fr"): string {
  try {
    const parsedDate = typeof date === "string" ? parseISO(date) : date;
    const localeObj = locale === "be" ? be : fr;
    return format(parsedDate, formatPatterns.long.dashboard, { locale: localeObj });
  } catch (_error) {
    return "Erreur de date";
  }
}
export function formatDeliveryTableDate(date: Date | string, locale: DateLocale = "fr"): string {
  try {
    const parsedDate = typeof date === "string" ? parseISO(date) : date;
    const localeObj = locale === "be" ? be : fr;
    return format(parsedDate, formatPatterns.short.table, { locale: localeObj });
  } catch (_error) {
    return "Erreur de date";
  }
}
export function formatTimeDate(date: Date | string, locale: DateLocale = "fr"): string {
  try {
    const parsedDate = typeof date === "string" ? parseISO(date) : date;
    const localeObj = locale === "be" ? be : fr;
    return format(parsedDate, formatPatterns.long.datetime, { locale: localeObj });
  } catch (_error) {
    return "Erreur de date";
  }
}

export function formatShortDate(date: Date | string, locale: DateLocale = "fr") {
  return dateFormat(date, { locale, format: "short", withTime: false });
}

export function formatLongDate(date: Date | string, locale: DateLocale = "fr") {
  return dateFormat(date, { locale, format: "long", withTime: false });
}

export function formatShortDateTime(date: Date | string, locale: DateLocale = "fr") {
  return dateFormat(date, { locale, format: "short", withTime: true });
}

export function formatLongDateTime(date: Date | string, locale: DateLocale = "fr") {
  return dateFormat(date, { locale, format: "long", withTime: true });
}

export function formatDateTimeLocalInput(value?: Date | string | null): string {
  if (value == null || value === "") return "";
  const date = typeof value === "string" ? parseISO(value) : value;
  return format(date, "yyyy-MM-dd'T'HH:mm");
}

export default dateFormat;
